import { Trans, useTranslation } from 'react-i18next';
import React, { useMemo, useState, useCallback, ReactNode } from 'react';
import { useHistory } from 'react-router-dom';

import { Button, L2PayModal, Input, ModalStepProps } from '@/components';
import { SETTLEMENT_ADDRESS } from '@/consts';
import { useCurrentAddressAccount } from '@/hooks';
import { useCurrentUserId } from '@/utils';

import { useArticleTip } from '../../hooks';
import style from './index.module.scss';

export const DepositModal = ({ id, turnOff }: { id: number; turnOff: () => void }) => {
  const { t } = useTranslation();

  const [amount, setAmount] = useState<string>();
  const currentAddress = useCurrentUserId();
  const { mutateAsync } = useArticleTip(id);

  const txParams = useMemo(
    () =>
      amount && currentAddress
        ? {
            price: Math.round(+amount * 10000),
            message: `Pay $${amount} to the promotion pool of Stream post ​https://debank.com/stream/${id}`,
            senderAddr: currentAddress,
            receiverAddr: SETTLEMENT_ADDRESS,
          }
        : undefined,
    [amount, currentAddress, id],
  );

  const payTitle = useMemo(
    () => (
      <div className={style.payTitle}>
        <div>
          <Trans i18nKey="ArticleTip_DepositModal.fs8ofy" values={{ amount: `$${amount}` }}>
            Pay <span className={style.payTitleWeight}>{amount}</span> to the promotion pool of
            Stream post
          </Trans>
        </div>
        <span className={style.payTitleWeight}>https://debank.com/stream/{id}</span>
      </div>
    ),
    [amount, id],
  );

  const handlePaySubmit = useCallback(
    async ({ tx }) => {
      mutateAsync(tx);
    },
    [mutateAsync],
  );

  return (
    <L2PayModal
      turnOff={turnOff}
      txParams={txParams}
      payTitle={payTitle}
      onPaySubmit={handlePaySubmit}
      feeTip={t('ArticleTip_DepositModal.1ugwbfb')}>
      <DepositInputCard setAmount={setAmount} />
    </L2PayModal>
  );
};

const DepositInputCard = ({
  showNext,
  setAmount,
  onClose,
}: ModalStepProps<null> & { setAmount: (_?: string) => void }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const { data: myAccountInfo, isLoading } = useCurrentAddressAccount();
  const [innerAmount, setInnerAmount] = useState<string>();
  const [errorMsg, setErrorMsg] = useState<ReactNode>(null);

  const insufficientBalance = useMemo(() => {
    const amount = Number(innerAmount);

    return !!innerAmount?.length && (!myAccountInfo?.balance || amount > myAccountInfo?.balance);
  }, [innerAmount, myAccountInfo?.balance]);

  const isValid = useMemo(
    () => !!innerAmount?.length && !insufficientBalance,
    [innerAmount, insufficientBalance],
  );

  const handleSubmit = useCallback(() => {
    if (!isValid) {
      return;
    }
    const amount = Number(innerAmount);
    if (!amount || amount < 0.01) {
      setErrorMsg(t('ArticleTip_DepositModal.min', { price: '$0.01' }));

      return;
    } else {
      setErrorMsg('');
    }

    setAmount(amount.toFixed(2));
    showNext?.();
  }, [showNext, innerAmount, setAmount, isValid, t]);

  return (
    <div className={style.depositModal}>
      <div className={style.depositModalTitle}>{t('ArticleTip_DepositModal.1wryqlm')}</div>
      <div className={style.depositModalContent}>
        <div className={style.depositModalDesc}>
          <span className={style.depositModalDescTitle}>
            {t('ArticleTip_DepositModal.1ts777e')}
          </span>
          <span className={style.depositModalDescAmount}>
            {t('ArticleTip_DepositModal.1kcqozs')}
            {myAccountInfo?.balance}
          </span>
        </div>
        <Input
          placeholder=""
          className={style.depositModalInput}
          value={innerAmount}
          onChange={setInnerAmount}
          prefix={<span className={style.inputPrefix}>$</span>}
          type="number"
          onPressEnter={handleSubmit}
          autoFocus
        />
        <div className={style.depositErrMsg}>
          {errorMsg ||
            (insufficientBalance ? (
              <div className={style.depositErrMsg}>
                {t('L2PayModal.31j7iz')}{' '}
                <span className={style.balance}>${myAccountInfo?.balance}</span>
              </div>
            ) : null)}
        </div>
      </div>
      <div className={style.depositModalFooter}>
        <Button type="default" className={style.footerButton} onClick={onClose}>
          {t('ArticleTip_DepositModal.x9d2fu')}
        </Button>
        {insufficientBalance ? (
          <Button
            type="primary"
            className={style.footerButton}
            onClick={() => history.push('/account')}>
            {t('L2PayModal.u5swep')}
          </Button>
        ) : (
          <Button
            type="primary"
            disabled={isLoading || !isValid}
            className={style.footerButton}
            onClick={handleSubmit}>
            {t('ArticleTip_DepositModal.1pqg')}
          </Button>
        )}
      </div>
    </div>
  );
};
